import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "react-bootstrap";
import { getDataAPI } from "../../../services/apiWrapper";
import { ALERT } from "../../../store/actions/actionTypes";
import { exportToCSV } from "../../../utils/exportCsv";
import CourseCascade from "./CourseCascade";

/**
 * LMS - Part 9: Reports (Council/Admin + Faculty).
 *
 * Pick a course (Trade -> Sub-Trade -> Course). Headline counts + an exam-wise
 * table + a per-student table (video progress %, exam stage, best result), with
 * a CSV export of the student table. Faculty see only their assigned courses.
 */
export default function Reports() {
  const dispatch = useDispatch();
  const token = () => localStorage.getItem("adminToken");
  const alert = (payload) => dispatch({ type: ALERT, payload });

  const [overview, setOverview] = useState(null);
  const [rows, setRows] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [loading, setLoading] = useState(false);

  const onCourse = async (cid) => {
    setOverview(null);
    setRows([]);
    setCourseId(cid);
    if (!cid) return;
    setLoading(true);
    try {
      const [ov, st] = await Promise.all([
        dispatch(getDataAPI(`admin/lms/report/overview?course_id=${cid}`, token())),
        dispatch(getDataAPI(`admin/lms/report/students?course_id=${cid}`, token())),
      ]);
      setOverview(ov.data.data);
      setRows(st.data.data || []);
    } catch (e) {
      alert({ error: "Could not load reports." });
    }
    setLoading(false);
  };

  const exportStudents = () => {
    if (!rows.length) return;
    const flat = rows.map((r, i) => ({
      "#": i + 1,
      Name: r.name,
      "Roll No": r.roll_number,
      "Progress %": r.progress_percent,
      "Lectures": `${r.lectures_done}/${r.lectures_total}`,
      "Exam Status": r.exam_status,
      "Best %": r.best_percent === null ? "-" : r.best_percent,
      Result: r.result,
    }));
    exportToCSV(flat, `lms-report-course-${courseId}`);
  };

  const Card = ({ label, value }) => (
    <div className="col-md-2 col-4 mb-2">
      <div className="bg-white text-center" style={{ border: "1px solid #e3e6ef", borderRadius: 6, padding: "12px 6px" }}>
        <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
        <div className="text-muted" style={{ fontSize: 12 }}>{label}</div>
      </div>
    </div>
  );

  return (
    <div className="container-fluid" style={{ padding: 20 }}>
      <h4 className="mb-3">Reports</h4>

      <CourseCascade onCourse={(id) => onCourse(id)} />

      {loading && <p className="text-muted">Loading…</p>}

      {overview && (
        <>
          <div className="row mb-3">
            <Card label="Students" value={overview.students_total} />
            <Card label="Exams" value={overview.exams_total} />
            <Card label="Appeared" value={overview.appeared} />
            <Card label="Passed" value={overview.passed} />
            <Card label="Pass %" value={`${overview.pass_percent}%`} />
            <Card label="Avg %" value={`${overview.avg_percent}%`} />
          </div>

          <h5>Exam-wise</h5>
          <table className="table table-bordered bg-white mb-4">
            <thead>
              <tr>
                <th>Exam</th><th>Date</th><th>Registered</th><th>Appeared</th><th>Passed</th><th>Avg %</th>
              </tr>
            </thead>
            <tbody>
              {overview.examwise.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-muted">No exams set up for this course.</td></tr>
              ) : (
                overview.examwise.map((e) => (
                  <tr key={e.id}>
                    <td>{e.title}</td>
                    <td>{e.exam_date || "—"}</td>
                    <td>{e.registered}</td>
                    <td>{e.appeared}</td>
                    <td>{e.passed}</td>
                    <td>{e.avg_percent}%</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 style={{ margin: 0 }}>Students ({rows.length})</h5>
            <Button size="sm" variant="outline-secondary" disabled={!rows.length} onClick={exportStudents}>
              Export CSV
            </Button>
          </div>
          <table className="table table-bordered bg-white">
            <thead>
              <tr>
                <th>#</th><th>Name</th><th>Roll No</th><th>Progress</th><th>Exam Status</th><th>Best %</th><th>Result</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-muted">No students in this course.</td></tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={r.student_id}>
                    <td>{i + 1}</td>
                    <td>{r.name}</td>
                    <td>{r.roll_number}</td>
                    <td>
                      {r.progress_percent}%
                      <span className="text-muted" style={{ fontSize: 11 }}> ({r.lectures_done}/{r.lectures_total})</span>
                    </td>
                    <td>{r.exam_status}</td>
                    <td>{r.best_percent === null ? "—" : `${r.best_percent}%`}</td>
                    <td>{r.result}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </>
      )}

      {!overview && !loading && <p className="text-muted">Choose a course to see its report.</p>}
    </div>
  );
}
